import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AttendListItem, AttendListResponse } from "~/types/common";

export const BUSINESS_MAIN_QUERY_KEYS = {
  homeSummary: ["business", "main", "home-summary"],
  attendList: ["business", "main", "attend-list"],
  calendar: ["business", "main", "calendar"],
} as const;

// 홈 대시보드 요약
export interface BusinessHomeSummary {
  todayReservationCount: number;
  pendingReservationCount: number;
  pendingContractCount: number;
  /** 만료예정(3일 이내, 잔여 1개 이상) 이용권 보유 반려동물 수 (구버전 응답에는 없을 수 있음) */
  expiringTicketPetCount?: number | null;
  attend: { totalCount: number; completeCount: number } | null;
  dismiss: { totalCount: number; completeCount: number } | null;
  todayPets: Array<{
    petSeq: number;
    /** 예약 상세 페이지 이동용 (구버전 응답에는 없을 수 있음) */
    reservationSeq?: number | null;
    petName: string;
    thumbnailUrl: string | null;
    cdAttendStatus: string;
  }>;
  weeklyReservations: Array<{ date: string; count: number; closed?: boolean }>;
}

const getBusinessHomeSummary = async (): Promise<BusinessHomeSummary> => {
  const { data } = await privateHttp.get(
    API_ENDPOINTS.BUSINESS_MAIN.HOME_SUMMARY
  );
  return data.data;
};

export const useBusinessHomeSummary = (enabled: boolean = true) => {
  return useQuery({
    queryKey: [...BUSINESS_MAIN_QUERY_KEYS.homeSummary],
    queryFn: getBusinessHomeSummary,
    enabled,
    staleTime: 0, // 홈 진입 시마다 refetch (캐시 먼저 표시 후 백그라운드 갱신)
  });
};

export const BUSINESS_RESERVE_QUERY_KEYS = {
  reservTypeList: ["business", "reserve", "reservType-list"],
} as const;

// attend-list 조회 함수
const getBusinessAttendList = async (
  attendStatus?: string
): Promise<AttendListResponse> => {
  try {
    const params = attendStatus ? { attendStatus } : {};
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MAIN.ATTEND_LIST,
      { params }
    );
    return data || { data: [] };
  } catch (error) {
    console.error("출석 목록 조회 실패:", error);
    throw error;
  }
};

// attend-list useQuery hook
export const useBusinessAttendList = (attendStatus?: string) => {
  return useQuery({
    queryKey: [...BUSINESS_MAIN_QUERY_KEYS.attendList, attendStatus],
    queryFn: () => getBusinessAttendList(attendStatus),
    staleTime: 0, // 항상 fresh 데이터로 취급
    gcTime: 1000 * 60 * 5, // 5분
    refetchOnWindowFocus: false, // 윈도우 포커스 시 자동 리페치 방지
    retry: 1, // 재시도 횟수 제한
  });
};

// 출석 상태 업데이트 요청 타입
// export interface UpdateAttendRequest {
//   reservDetailSeqs: number[];
//   attendType: "등원" | "하원";
//   action: "등원" | "하원" | "하원취소";
// }

// 출석 상태 업데이트 함수
const updateBusinessAttend = async (
  request: AttendListItem[]
): Promise<{ success: boolean; message: string }> => {
  try {
    const { data } = await privateHttp.patch(
      API_ENDPOINTS.BUSINESS_MAIN.UPDATE_ATTEND,
      request
    );
    return {
      success: true,
      message: data.message || "출석 상태가 성공적으로 업데이트되었습니다.",
    };
  } catch (error) {
    console.error("출석 상태 업데이트 실패:", error);
    throw error;
  }
};

// 출석 상태 업데이트 useMutation hook
export const useUpdateBusinessAttend = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBusinessAttend,
    onSuccess: () => {
      // 출석 목록 캐시 무효화하여 새로운 데이터를 불러오기
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MAIN_QUERY_KEYS.attendList,
      });
    },
    onError: (error) => {
      console.error("출석 상태 업데이트 실패:", error);
    },
  });
};

/** 캘린더 props - 예약 유형별 요약 */
export interface BusinessCalendarSummaryItem {
  reservType: string;
  summary: number;
}

/** 캘린더 props - 횟수권 등 이용권 보유 현황 */
export interface BusinessCalendarTicketItem {
  userName: string;
  petName: string;
  ticketName: string;
  totalQty?: number;
  remainQty?: number;
  cdProductType?: string;
}

export type BusinessCalendarProp =
  | { type: "summaries"; datas: BusinessCalendarSummaryItem[] }
  | { type: "tickets"; datas: BusinessCalendarTicketItem[] }
  | { type: string; datas: unknown[] };

export interface BusinessCalendarData {
  calendar: Record<string, unknown>;
  props: BusinessCalendarProp[];
}

// calendar 조회 함수
const getBusinessCalendar = async (params?: {
  yearMonthDay?: string;
}): Promise<BusinessCalendarData> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MAIN.CALENDAR,
      { params }
    );
    return data.data || { calendar: {}, props: [] };
  } catch (error) {
    console.error("캘린더 조회 실패:", error);
    throw error;
  }
};

// calendar useQuery hook
export const useBusinessCalendar = (params?: { yearMonthDay?: string }) => {
  return useQuery({
    queryKey: [...BUSINESS_MAIN_QUERY_KEYS.calendar, params],
    queryFn: () => getBusinessCalendar(params),
    staleTime: 0, // 항상 새로운 데이터 요청
    gcTime: 1000 * 60 * 10, // 10분
    refetchOnWindowFocus: true, // 윈도우 포커스 시 재요청
    refetchOnMount: true, // 컴포넌트 마운트 시 재요청
    retry: 1,
  });
};

// reservType-list 조회 함수
const getBusinessReservTypeList = async (): Promise<{ data: unknown }> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_RESERVE.RESERV_TYPE_LIST
    );
    return data || { data: [] };
  } catch (error) {
    console.error("예약 타입 목록 조회 실패:", error);
    throw error;
  }
};

// reservType-list useQuery hook
export const useBusinessReservTypeList = () => {
  return useQuery({
    queryKey: BUSINESS_RESERVE_QUERY_KEYS.reservTypeList,
    queryFn: getBusinessReservTypeList,
    staleTime: 1000 * 60 * 10, // 10분
    gcTime: 1000 * 60 * 30, // 30분
    refetchOnWindowFocus: false,
    retry: 1,
  });
};
